import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/core/redux/store';
import { Authenticating } from '@/modules/auth/components/Authenticating';
import { SessionData } from '@/modules/auth/types';
import { useCore } from '../hooks/useCore';

// Types for auth guard configuration
export interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
  roles?: string[];
  permissions?: string[];
}

export interface PublicOnlyRouteProps {
  children: React.ReactNode;
}

/**
 * Higher-order component for protecting routes that require authentication
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children, fallback }) => {
  const { sessionData, loading } = useSelector(
    (state: RootState) => state.auth.main
  );
  const { gotoPtEApp } = useCore();

  const location = useLocation();

  // Show a small spinner while auth is loading to avoid premature redirect
  if (loading) {
    return <>{fallback ?? <Authenticating />}</>;
  }

  // redirect to PTE app if no session data or user
  if (!sessionData || !sessionData.Client || !sessionData.User) {
    gotoPtEApp();
    return null;
  }

  // validate subscription status
  const redirect = validateSubscriptionStatus(
    sessionData as unknown as SessionData,
    location,
    gotoPtEApp
  );
  if (redirect) {
    return redirect;
  }

  return <>{children}</>;
};

const validateSubscriptionStatus = (
  sessionData: SessionData,
  location: ReturnType<typeof useLocation>,
  gotoPtEApp: () => void
): React.ReactNode | null => {
  const clientData = sessionData.Client;
  const subscriptionInfo = clientData?.SubscriptionInfo;

  // redirect to PTE app if no subscription info
  if (!subscriptionInfo) {
    gotoPtEApp();
    return null;
  }

  // redirect to subscription plan page
  const status = subscriptionInfo?.Status as string;
  const pickPlanStatuses = [
    'no_subscription',
    'trial_expired',
    'suspended',
    'cancelled',
    'trial',
  ];
  if (
    pickPlanStatuses.includes(status) &&
    location.pathname !== '/subscription/plan'
  ) {
    return <Navigate to='/subscription/plan' replace />;
  }

  // // redirect to subscription details page
  // const subscriptionDetailsStatuses = [
  //   'active',
  //   'active_upcoming',
  //   'active_cancelled',
  //   'inactive',
  // ];
  // if (
  //   subscriptionDetailsStatuses.includes(status) &&
  //   ['/', '/subscription/plan'].includes(location.pathname)
  // ) {
  //   return <Navigate to='/subscription/details' replace />;
  // }

  // // redirect to subscription details > change payment method
  // const changeSubscriptionStatuses = ['suspended_due', 'failed_payment'];
  // if (
  //   changeSubscriptionStatuses.includes(status) &&
  //   location.pathname !== '/subscription/details'
  // ) {
  //   return <Navigate to='/subscription/details' replace />;
  // }

  return null;
};
